import type { KeyboardEvent, ReactNode, Ref } from 'react'
import { useImperativeHandle, useState } from 'react'

import { Button } from '@/components/button'
import { faChevronDown, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'

import { SUB_TIMER_LIMITS } from '~shared/rundown/cue'
import { hmsToMilliseconds, millisecondsToHms } from '~shared/timeutils'

import { EditableHms } from '../timing/EditableHms'

import type {
  SubTimerDirection,
  SubTimerEndAction,
  SubTimerInput,
} from './sub-timers'

// Mirrors the shell of the cue timing popovers
export const SUB_TIMER_POPOVER_CLASS =
  'w-60 gap-0 rounded-lg border border-white/10 bg-black/80 p-2.5 text-white shadow-lg ring-0 backdrop-blur-lg'

/** Lets the popover save the form when it closes, as popovers do elsewhere. */
export type SubTimerFormHandle = {
  /** Submit, but only when something changed from `initial`. */
  commit: () => void
}

type SubTimerFormProps = {
  ref?: Ref<SubTimerFormHandle>
  title: string
  initial?: SubTimerInput
  submitLabel: string
  onSubmit: (input: SubTimerInput) => void
  onCancel: () => void
  onDelete?: () => void
}

const DEFAULT_INPUT: SubTimerInput = {
  name: '',
  direction: 'down',
  duration: 0,
  endAction: 'stop',
  autoStart: false,
  showOnOutput: false,
}

const DIRECTIONS: { value: SubTimerDirection; label: string }[] = [
  { value: 'down', label: 'Count down' },
  { value: 'up', label: 'Count up' },
]

const END_ACTIONS: { value: SubTimerEndAction; label: string }[] = [
  { value: 'stop', label: 'Stop' },
  { value: 'continue', label: 'Keep counting' },
]

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="flex rounded-md bg-gray-800 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'h-6 flex-1 rounded-sm text-xs transition-colors',
            value === o.value
              ? 'bg-gray-600 text-white shadow-sm'
              : 'text-white/50 hover:text-white/80',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function OptionRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="flex h-7 cursor-pointer items-center justify-between gap-3 text-xs text-white/70">
      {label}
      {children}
    </label>
  )
}

export function SubTimerForm({
  ref,
  title,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}: SubTimerFormProps) {
  const start = initial ?? DEFAULT_INPUT
  const [name, setName] = useState(start.name)
  const [direction, setDirection] = useState<SubTimerDirection>(start.direction)
  const [duration, setDuration] = useState(start.duration)
  const [endAction, setEndAction] = useState<SubTimerEndAction>(start.endAction)
  const [autoStart, setAutoStart] = useState(start.autoStart)
  const [showOnOutput, setShowOnOutput] = useState(start.showOnOutput)

  const values = (): SubTimerInput => ({
    name: name.trim(),
    direction,
    duration,
    endAction,
    autoStart,
    showOnOutput,
  })
  const submit = () => onSubmit(values())

  useImperativeHandle(ref, () => ({
    commit() {
      const next = values()
      const changed = (Object.keys(next) as (keyof SubTimerInput)[]).some(
        (key) => next[key] !== start[key],
      )
      if (changed) submit()
    },
  }))

  // Enter saves from any field. Buttons keep Enter for themselves.
  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter' || e.target instanceof HTMLButtonElement) return
    e.preventDefault()
    submit()
  }

  return (
    <form
      className="flex flex-col gap-3"
      onKeyDown={onKeyDown}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        {onDelete && (
          <Button
            icon={faTrash}
            size="sm"
            colour="none"
            title="Remove timer"
            buttonClass="text-white/40 hover:text-red-400"
            onClick={onDelete}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <input
          autoFocus
          placeholder="Name"
          maxLength={SUB_TIMER_LIMITS.NAME_LENGTH}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-form-type="other"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-7 w-full rounded-sm bg-gray-800 px-2 text-sm placeholder:text-white/40 hover:brightness-125 focus:ring-3 focus:outline-hidden"
        />
        <Segmented
          options={DIRECTIONS}
          value={direction}
          onChange={setDirection}
        />
        {direction === 'down' && (
          <EditableHms
            hms={millisecondsToHms(duration)}
            onUpdate={(hms) => setDuration(hmsToMilliseconds(hms))}
          />
        )}
      </div>

      <div className="flex flex-col border-t border-white/10 pt-2">
        {direction === 'down' && (
          <OptionRow label="At 00:00">
            <span className="relative">
              <select
                value={endAction}
                onChange={(e) =>
                  setEndAction(e.target.value as SubTimerEndAction)
                }
                className="h-6 cursor-pointer appearance-none rounded-sm bg-gray-800 pr-6 pl-2 text-xs text-white hover:brightness-125 focus:ring-3 focus:outline-hidden"
              >
                {END_ACTIONS.map((a) => (
                  <option
                    key={a.value}
                    value={a.value}
                  >
                    {a.label}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-[8px] text-white/50"
              />
            </span>
          </OptionRow>
        )}
        <OptionRow label="Start with cue">
          <input
            type="checkbox"
            checked={autoStart}
            onChange={(e) => setAutoStart(e.target.checked)}
            className="size-3.5 cursor-pointer accent-blue-500"
          />
        </OptionRow>
        <OptionRow label="Show on output">
          <input
            type="checkbox"
            checked={showOnOutput}
            onChange={(e) => setShowOnOutput(e.target.checked)}
            className="size-3.5 cursor-pointer accent-blue-500"
          />
        </OptionRow>
      </div>

      <div className="flex gap-2.5">
        <Button
          className="grow px-2"
          size="sm"
          text="Cancel"
          colour="dark"
          onClick={onCancel}
        />
        <Button
          className="grow px-2"
          size="sm"
          text={submitLabel}
          colour="dark"
          type="submit"
        />
      </div>
    </form>
  )
}
